import React, { useEffect, useState } from "react";
import { Terminal, Copy, Check, KeyRound, Bot, Sparkles } from "lucide-react";

const methodColor = {
  GET: "bg-emerald-100 text-emerald-700",
  POST: "bg-blue-100 text-blue-700",
  PATCH: "bg-amber-100 text-amber-700",
  PUT: "bg-amber-100 text-amber-700",
  DELETE: "bg-red-100 text-red-700",
};

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");
const install = `# From this repository, in your Python virtual environment\npython -m pip install -e .\nexport AGP_URL=${BACKEND_URL}\nbuddy login --email alex@hermes.ai`;

const cli = [
  { cmd: "buddy api list", desc: "Discover every API operation" },
  { cmd: "buddy api content.update_post --help", desc: "Show current fields and arguments" },
  { cmd: "buddy api content.update_post --id post_1 --json @post.json", desc: "Edit content from a JSON file" },
  { cmd: "buddy content approve post_1", desc: "Approve a content post" },
  { cmd: "buddy api social.list_profiles", desc: "List social profiles" },
  { cmd: "buddy people list", desc: "List people" },
  { cmd: "buddy seq enroll seq_1 --person per_3", desc: "Draft a personalized sequence" },
  { cmd: "buddy seq review enr_ab12", desc: "Show drafted steps awaiting approval" },
  { cmd: "buddy seq approve enr_ab12 --step step_1 --send", desc: "Approve & send a step" },
  { cmd: "buddy content draft --channel twitter 'AI SDRs'", desc: "Draft a Twitter post" },
  { cmd: "buddy tasks list", desc: "List agent tasks" },
];

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <button onClick={copy} className="p-1.5 rounded-md text-[hsl(240,4%,55%)] hover:bg-white/10 hover:text-white transition-colors">
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

export default function ApiPage() {
  const [tab, setTab] = useState("api");
  const [endpoints, setEndpoints] = useState([]);
  const [schemaError, setSchemaError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${BACKEND_URL}/openapi.json`, { signal: controller.signal })
      .then((response) => { if (!response.ok) throw new Error("Schema unavailable"); return response.json(); })
      .then((schema) => setEndpoints(Object.entries(schema.paths).flatMap(([path, methods]) => Object.entries(methods)
        .filter(([method]) => ["get", "post", "put", "patch", "delete", "head", "options", "trace"].includes(method))
        .map(([method, operation]) => ({ method: method.toUpperCase(), path, desc: operation.summary, operation: operation.operationId })))))
      .catch((error) => { if (error.name !== "AbortError") setSchemaError(true); });
    return () => controller.abort();
  }, []);
  const curl = `# 1) Login\nTOKEN=$(curl -s -X POST ${BACKEND_URL}/api/auth/login \\\n  -H "Content-Type: application/json" \\\n  -d '{"email":"alex@hermes.ai","password":"hermes123"}' | jq -r .access_token)\n\n# 2) Enroll a person - Hermes drafts a personalized cadence\ncurl -X POST ${BACKEND_URL}/api/sequences/seq_1/enroll \\\n  -H "Authorization: Bearer $TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d '{"person_id":"per_3"}'`;

  return (
    <div className="p-6 max-w-[1000px] mx-auto animate-in-up">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-lg brand-bg-soft flex items-center justify-center shrink-0"><Bot className="h-5 w-5 text-[hsl(245,50%,48%)]" /></div>
        <div>
          <h1 className="text-[20px] font-semibold">Buddy Terminal</h1>
          <p className="text-[13.5px] text-[hsl(240,4%,48%)] mt-0.5">Operate BuddyOS through the REST API or the buddy CLI. Both surfaces follow the live OpenAPI schema.</p>
        </div>
      </div>

      <div className="rounded-xl border border-[hsl(240,6%,91%)] bg-white p-4 mb-4 flex items-center gap-3">
        <KeyRound className="h-5 w-5 text-[hsl(240,4%,45%)]" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium">Authentication</div>
          <code className="text-[12.5px] text-[hsl(240,4%,50%)] font-mono">Bearer JWT from POST /api/auth/login</code>
        </div>
        <a href={`${BACKEND_URL}/docs`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] font-medium text-[hsl(245,50%,45%)]"><Sparkles className="h-3.5 w-3.5" />Open API docs</a>
      </div>

      <div className="flex items-center gap-1 border-b border-[hsl(240,6%,92%)] mb-4">
        {[["api", "REST API"], ["cli", "Terminal CLI"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px ${tab === k ? "border-[hsl(245,58%,60%)] text-[hsl(240,6%,10%)]" : "border-transparent text-[hsl(240,4%,50%)] hover:text-[hsl(240,6%,10%)]"}`}>{l}</button>
        ))}
      </div>

      {tab === "api" ? (
        <>
          <div className="rounded-xl border border-[hsl(240,6%,91%)] bg-white overflow-auto max-h-[460px] mb-4">
            {endpoints.length === 0 && <p className="p-4 text-[13px]" role={schemaError ? "alert" : "status"}>{schemaError ? "Could not load API operations. Check the backend connection and reload this page." : "Loading API operations…"}</p>}
            {endpoints.map((e, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b border-[hsl(240,6%,94%)] last:border-b-0 hover:bg-[hsl(240,20%,99%)]">
                <span className={`inline-flex justify-center w-14 rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${methodColor[e.method]}`}>{e.method}</span>
                <div><code className="text-[12.5px] font-mono">{e.path}</code><div className="text-[11px] text-[hsl(240,4%,50%)]">{e.operation}</div></div>
                <span className="ml-auto text-[12.5px] text-[hsl(240,4%,50%)] text-right">{e.desc}</span>
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-[hsl(240,8%,12%)] overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10">
              <Terminal className="h-4 w-4 text-[hsl(240,4%,65%)]" />
              <span className="text-[12px] font-medium text-[hsl(240,4%,70%)]">Example — login then enroll a person</span>
              <div className="ml-auto"><CopyBtn text={curl} /></div>
            </div>
            <pre className="p-4 text-[12.5px] font-mono text-[hsl(150,40%,75%)] overflow-x-auto whitespace-pre">{curl}</pre>
          </div>
        </>
      ) : (
        <>
          <div className="rounded-xl bg-[hsl(240,8%,12%)] overflow-hidden mb-4">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10">
              <Terminal className="h-4 w-4 text-[hsl(240,4%,65%)]" /><span className="text-[12px] font-medium text-[hsl(240,4%,70%)]">Install</span>
              <div className="ml-auto"><CopyBtn text={install} /></div>
            </div>
            <pre className="p-4 text-[12.5px] font-mono text-[hsl(150,40%,75%)] overflow-x-auto">{install}</pre>
          </div>
          <p className="mb-4 text-[13px] text-[hsl(240,4%,48%)]">Use --json @file.json or --json - for stdin. Commands return JSON; errors go to stderr with a nonzero exit code. Content publishing currently records published status; it does not post to social networks.</p>
          <div className="rounded-xl border border-[hsl(240,6%,91%)] bg-white overflow-hidden">
            {cli.map((c, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b border-[hsl(240,6%,94%)] last:border-b-0 hover:bg-[hsl(240,20%,99%)]">
                <code className="text-[12.5px] font-mono text-[hsl(245,50%,45%)]">$ {c.cmd}</code>
                <span className="ml-auto text-[12.5px] text-[hsl(240,4%,50%)] shrink-0">{c.desc}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
